import { isPlatformBrowser } from '@angular/common'
import { inject, Injectable, PLATFORM_ID } from '@angular/core'
import { isDefined } from '@pai/commons'
import { init, track } from '@plausible-analytics/tracker'

import { CLIENT_CONFIG } from '../client-config/client-config.token'
import { CURRENT_LANG } from '../i18n/language.token'
import { LoggerService } from '../logger/logger.service'
import { AnalyticsService } from './analytics.service'
import { AnalyticsEvent } from './analytics-event.enum'

type Props = Record<string, string | number | null | boolean>

@Injectable()
export class PlausibleAnalytics extends AnalyticsService {
  /**
   *  locally we use the angular/webpack server proxy to redirect to plausible.io;
   * on a deployed system the cloudfront origin has the corresponding rule
   */
  private static readonly ENDPOINT = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/event`

  private readonly currentLanguage = inject(CURRENT_LANG)
  private readonly logger = inject(LoggerService).getInstance('PlausibleAnalytics')
  /**
   *  the domain to track on
   *  for xx/pr systems with subdomain (www-xx1|www-pr1). for prod no subdomain
   */
  private readonly domain = inject(CLIENT_CONFIG).origin.replace(/^(https?:\/\/)(www\.)?/, '')

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) {
      throw new Error('this service can only be used on platform browser')
    }
    super()

    init({
      endpoint: PlausibleAnalytics.ENDPOINT,
      domain: this.domain,
      autoCapturePageviews: false,
      outboundLinks: true,
      captureOnLocalhost: false,
      customProperties: () => {
        const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
        const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)

        return {
          ln: this.currentLanguage,
          vp: `${vw}x${vh}`,
          ul: window.navigator.language,
        }
      },
    })
  }

  trackPageView(path: string): void {
    path = path.replace(/\/$/, '')
    this.logger.debug('trackPageView', path)
    const url = [window.location.origin, path, window.location.search].join('')
    track(AnalyticsEvent.PAGE_VIEW, { url })
  }

  trackTiming(name: AnalyticsEvent, time: number): void {
    this.logger.debug('trackTiming', name, time)
    track(name, { props: { [name]: `${time}` } })
  }

  trackEvent(name: AnalyticsEvent, props: Props = {}): void {
    this.logger.debug('trackEvent', name, props)
    const stringProps: Record<string, string> = {}
    for (const [key, value] of Object.entries(props)) {
      if (isDefined(value)) {
        stringProps[key] = `${value}`
      }
    }
    track(name, { props: stringProps })
  }
}
